import { PriorityHigh } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import { PendingLeaderboardActionArea } from "components/events/judging/pending/PendingLeaderboardActionArea";
import { PendingLeaderboardItem } from "components/events/judging/pending/PendingLeaderboardItem";
import { PendingLeaderboardActionItem } from "./PendingLeaderboardActionArea";
import { isPendingRide, usePendingResultChanges } from "components/events/judging/pending/usePendingResultChanges";
import {
    useHeatSummaryStyles
} from "components/layout/heat/heatStyles";
import { T } from "components/util/t";
import { PendingLeaderboardFields, Progression } from "components/events/judging/pending/usePendingLeaderboard";
import { Sx } from "utils/sx";
import { useCompetitorsAndResultsById } from "components/layout/heat/useCompetitorsById";
import { Fragment } from "react/jsx-runtime";
import { CutLine } from "components/layout/series/rankings/CutLine";
import { useTranslate } from "components/providers/LocalizationProvider";

const stickyHeadingStyles: Sx = (theme) => ({
    position: "sticky",
    top: 0,
    zIndex: 1,
    background: `linear-gradient(to bottom, ${theme.palette.background.paper} calc(100% - ${theme.spacing(3)}), transparent)`,
});

const listStyles: Sx = (theme) => ({
    borderBottom: `1px solid ${theme.palette.border.light}`,
    padding: 0,
    margin: 0,
    listStyle: "none",

    "& [data-tied=false] + [data-tied=true], & [data-tied=true]:first-of-type": {
        "--callout-display": "flex",
        cursor: "pointer",
        borderLeft: `2px solid ${theme.palette.error.main}`,
        borderRight: `2px solid ${theme.palette.error.main}`,
        borderTopLeftRadius: theme.spacing(0.75),
        borderTopRightRadius: theme.spacing(0.75),
    },

    // any subsequent items in the tied group
    "& [data-tied=true] + [data-tied=true]": {
        cursor: "pointer",
        borderLeft: `2px solid ${theme.palette.error.main}`,
        borderRight: `2px solid ${theme.palette.error.main}`,
    },

    // last item in a tied group
    "& [data-last-tied=true]": {
        borderBottom: `2px solid ${theme.palette.error.main}`,
        borderBottomLeftRadius: theme.spacing(0.75),
        borderBottomRightRadius: theme.spacing(0.75),
    },

    // first item after a tied group
    "& [data-tied=true] + [data-tied=false]": {
        borderTop: "1px solid transparent",
    },

    // any items in a non-tied group
    "& [data-tied=false] + [data-tied=false]": {
        borderTop: `1px solid ${theme.palette.border.light}`,
    },
});

const leaderboardHeadingStyles: Sx = (theme) => ({
    textTransform: "uppercase",
    py: 1,
    px: 2,
    borderBottom: `1px solid ${theme.palette.border.light}`,
});

export const PendingLeaderboardTable: React.FC<{
    leaderboard: PendingLeaderboardFields;
    leaderboardWithPending: PendingLeaderboardFields;
    progression: Progression;
    Header: React.ReactNode;
}> = ({ leaderboard, leaderboardWithPending, Header, progression }) => {
    const competitorsAndResultsById = useCompetitorsAndResultsById(
        leaderboardWithPending.competitors,
        leaderboard.result,
    );

    const allTies = leaderboardWithPending.result.filter((result, index, allResults) => 
        result.total &&
        (result.place === allResults[index - 1]?.place || result.place === allResults[index + 1]?.place)
    );

    const pendingTies = allTies.filter(tie => isPendingRide(tie, true));
    const pendingResults = leaderboardWithPending.result.filter(
        result => isPendingRide(result, true) && !allTies.some(tie => tie.athleteId === result.athleteId)
    );

    return (
        <>
            <Stack sx={stickyHeadingStyles}>
                {Header}
                {(pendingTies.length > 0 || pendingResults.length > 0) && <PendingLeaderboardActionArea>
                    <ActionItems pendingResults={pendingTies} competitorsAndResultsById={competitorsAndResultsById} heat={leaderboardWithPending} isTied />
                    <ActionItems pendingResults={pendingResults} competitorsAndResultsById={competitorsAndResultsById} heat={leaderboardWithPending} />
                </PendingLeaderboardActionArea>}
            </Stack>
            <Box component="ul" sx={listStyles}>
                {leaderboardWithPending.result.length === 0 ? (
                    <EmptyState />
                ) : (
                    <>
                        <Typography component="div" variant="label2" color="textSecondary" sx={leaderboardHeadingStyles}>Leaderboard</Typography>
                        {leaderboardWithPending.result.map((result, resultIndex) => {
                            const resultData = competitorsAndResultsById[result.athleteId];
                            if (!resultData) return null;
                        
                            const tied = allTies.some(tie => tie.athleteId === result.athleteId);
                            return (
                                <Fragment key={result.athleteId}>
                                    <ProgressionCutLine progression={progression} index={resultIndex} tied={tied} />

                                    <ResultRow
                                        competitor={resultData.competitor}
                                        result={resultData.result}
                                        pendingResult={result}
                                        heat={leaderboardWithPending}
                                        tied={tied}
                                        lastTied={allTies.length > 0 && allTies[allTies.length - 1].athleteId === result.athleteId}
                                    />
                                </Fragment>
                            );
                        })}
                    </>
                )}
            </Box>
        </>
    );
};

const ProgressionCutLine: React.FC<{
    progression: Progression;
    index: number;
    tied?: boolean;
}> = ({ progression, index, tied }) => {
    const cutLineIndex = progression.findIndex(progression => index === progression.position);
    const isFirstCutLine = cutLineIndex === 0;
    const description = useTranslate(isFirstCutLine ? "TOP" : "NEXT");

    if (cutLineIndex === -1) return null;

    const cutLine = progression[cutLineIndex];
    const cutLineDescription = `${description} ${cutLine.max}`;

    return <Box component="li" data-tied={tied}><CutLine description={cutLineDescription} tableRow={false} /></Box>;
};

const ActionItems: React.FC<{
    pendingResults: PendingLeaderboardFields["result"];
    competitorsAndResultsById: Record<string, {
        competitor: PendingLeaderboardFields["competitors"][number],
        result: PendingLeaderboardFields["result"][number]
    }>;
    heat: PendingLeaderboardFields;
    isTied?: boolean
}> = ({ pendingResults, competitorsAndResultsById, heat, isTied }) => {
    return pendingResults.map(result => {
        const resultData = competitorsAndResultsById[result.athleteId];
        if (!resultData) return null;

        return <ResultRow
            key={result.athleteId}
            competitor={resultData.competitor}
            result={resultData.result}
            pendingResult={result}
            heat={heat}
            tied={isTied}
            isActionItem
        />;
    });
};

const ResultRow: React.FC<{
    result: PendingLeaderboardFields["result"][number];
    pendingResult: PendingLeaderboardFields["result"][number];
    heat: PendingLeaderboardFields;
    competitor: PendingLeaderboardFields["competitors"][number];
    tied?: boolean;
    lastTied?: boolean;
    isActionItem?: boolean
}> = ({ result, pendingResult, heat, competitor, tied, lastTied, isActionItem }) => {
    const { isPending, place, formattedTotal } = usePendingResultChanges(result, pendingResult, heat.config);

    const showPlaceCaption = isPending && place.change;
    const showTotalCaption = isPending && formattedTotal.pending !== formattedTotal.approved;
    const caption = showPlaceCaption ? <>{place.change > 0 ? "↓" : "↑"} <T count={Math.abs(place.change)}>spots</T></> : "";
    const jersey = heat.config.jerseyOrder?.[competitor.position];

    const LeaderboardItemVariant = isActionItem ? PendingLeaderboardActionItem : PendingLeaderboardItem;
    
    return (
        <LeaderboardItemVariant
            place={place.pending}
            placeCaption={showPlaceCaption ? place.approved : ""}
            title={competitor.athlete.name}
            caption={caption}
            total={formattedTotal.pending}
            totalCaption={showTotalCaption ? formattedTotal.approved : ""}
            isPending={isPending}
            tied={tied}
            lastTied={lastTied}
            calloutText={tied ? <T>Approve tie?</T> : <T>Approve?</T>}
            calloutIcon={<PriorityHigh fontSize='small' />}
            calloutImpact={tied ? "high" : "medium"}
            jersey={jersey}
            bib={competitor.bib}
        />
    );
};

const EmptyState: React.FC = () => {
    const classes = useHeatSummaryStyles();

    return (
        <li className={classes.row}>
            <Typography variant="body1">
                <T>Athlete lineup is coming soon, stay tuned.</T>
            </Typography>
        </li>
    );
};
